import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { BookOpen, Star, Calendar, Play, Loader2, Lock, Unlock } from "lucide-react";

import { _t } from "@/lib/i18n";
const AGE_COLORS = {
  all_ages: "text-emerald-300 border-emerald-400/30 bg-emerald-500/5",
  age_13: "text-sky-300 border-sky-400/30 bg-sky-500/5",
  age_16: "text-amber-300 border-amber-400/30 bg-amber-500/5",
  age_18: "text-rose-300 border-rose-400/30 bg-rose-500/5",
};
const AGE_LABEL = { all_ages: _t("Per tutti"), age_13: "13+", age_16: "16+", age_18: "18+" };

function ChapterCard({ c, onOpen }) {
  return (
    <button data-testid={`chapter-${c.id}`} onClick={() => onOpen(c)} className="text-left rounded-2xl border border-white/10 bg-white/[0.02] p-5 hover:border-white/25 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/40">{_t("Capitolo · {0}", c.state === "IN_ANNUNCIO" ? _t("In arrivo") : _t("Pubblicato"))}</div>
          <h3 className="text-lg text-white font-display mt-1">{c.title}</h3>
          <div className="text-xs text-white/60 mt-1">{_t("di")}{" "}<span className="text-white/80">{c.author_pen_name}</span></div>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${AGE_COLORS[c.age_final] || "border-white/20 text-white/60"}`}>
          {AGE_LABEL[c.age_final] || "?"}
        </span>
      </div>
      <p className="text-sm text-white/70 mt-3 line-clamp-2">{c.synopsis}</p>
      <div className="flex items-center gap-4 mt-3 text-xs text-white/50">
        <span className="inline-flex items-center gap-1"><Star className="w-3 h-3" /> {c.reviews_avg_rating || "—"} ({c.reviews_count})</span>
        <span className="inline-flex items-center gap-1"><BookOpen className="w-3 h-3" /> {c.downloads_count}{" "}{_t("download")}</span>
      </div>
    </button>
  );
}

function ChapterModal({ chapter, onClose, onDownloaded }) {
  const [phase, setPhase] = useState("preview"); // preview | ad | body | error
  const [full, setFull] = useState(null);
  const [ad, setAd] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [rating, setRating] = useState(5);
  const [destination, setDestination] = useState("both");
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/racconti/chapters/${chapter.id}`);
        setFull(data);
        if (data.body) setPhase("body");
        const rs = await api.get(`/racconti/chapters/${chapter.id}/reviews`);
        setReviews(rs.data.reviews);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [chapter.id]);

  useEffect(() => {
    if (phase !== "ad" || !ad) return;
    setRemaining(ad.seconds_required);
    const iv = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(iv);
  }, [phase, ad]);

  const startAd = async () => {
    try {
      const { data } = await api.post(`/racconti/chapters/${chapter.id}/ad/start`);
      setAd(data); setPhase("ad");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const completeAd = async () => {
    try {
      await api.post(`/racconti/chapters/${chapter.id}/ad/complete?ad_id=${ad.ad_id}`);
      const { data } = await api.get(`/racconti/chapters/${chapter.id}`);
      setFull(data); setPhase("body");
      toast.success(_t("Capitolo sbloccato! Grazie della visione."));
      onDownloaded && onDownloaded();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitReview = async () => {
    if (reviewText.trim().length < 10) { toast.error(_t("Scrivi almeno 10 caratteri.")); return; }
    try {
      await api.post(`/racconti/chapters/${chapter.id}/reviews`, {
        rating, text: reviewText.trim(), destination,
      });
      toast.success(_t("Recensione pubblicata."));
      setReviewText("");
      const rs = await api.get(`/racconti/chapters/${chapter.id}/reviews`);
      setReviews(rs.data.reviews);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!full) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div data-testid="chapter-modal" className="max-w-3xl w-full max-h-[92vh] overflow-y-auto rounded-2xl bg-[#0F1A2E] border border-white/10 p-6 space-y-4">
        <div className="flex justify-between items-start gap-4">
          <div>
            <h2 className="text-2xl text-white font-display">{full.title}</h2>
            <Link to={`/racconti/autore/${full.author_slug}`} className="text-sm text-cyan-300 hover:underline">{_t("di {0}", full.author_pen_name)}</Link>
          </div>
          <button onClick={onClose} className="btn-ghost !p-2">✕</button>
        </div>
        <div className="text-white/70 text-sm">{full.synopsis}</div>

        {phase === "preview" && (
          <div className="space-y-3">
            <div className="p-4 rounded-xl bg-white/[0.03] border border-white/10">
              <div className="text-xs text-white/40 mb-2">{_t("Anteprima gratuita")}</div>
              <div className="text-white/85 whitespace-pre-wrap">{full.preview}</div>
            </div>
            <button data-testid="watch-ad-btn" onClick={startAd} className="btn-primary w-full inline-flex items-center justify-center gap-2">
              <Play className="w-4 h-4" />{" "}{_t("Guarda lo spot (15s) e sblocca il capitolo")}</button>
            <div className="text-[11px] text-white/40 text-center">{_t("Modello Pay-per-Attention: la visione dello spot finanzia direttamente l'autore.")}</div>
          </div>
        )}

        {phase === "ad" && ad && (
          <div className="space-y-3">
            <div data-testid="ad-placeholder" className="aspect-video rounded-xl bg-gradient-to-br from-cyan-500/20 via-indigo-500/20 to-violet-500/20 flex flex-col items-center justify-center border border-white/10">
              <div className="text-4xl font-display text-white">{remaining}s</div>
              <div className="text-xs text-white/60 mt-2">{ad.placeholder_message}</div>
            </div>
            <button data-testid="complete-ad-btn" onClick={completeAd} disabled={remaining > 0} className="btn-primary w-full disabled:opacity-40 inline-flex items-center justify-center gap-2">
              {remaining > 0 ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
              {remaining > 0 ? _t("Attendi ancora {0}s", remaining) : _t("Sblocca ora")}
            </button>
          </div>
        )}

        {phase === "body" && (
          <>
            <div data-testid="chapter-body" className="p-5 rounded-xl bg-white/[0.03] border border-white/10 whitespace-pre-wrap leading-relaxed text-white/90">
              {full.body}
            </div>

            <section className="pt-3 border-t border-white/5 space-y-3">
              <div className="text-white/70 text-sm">{_t("Recensione certificata (solo per chi ha scaricato)")}</div>
              <div className="flex items-center gap-2">
                {[1,2,3,4,5].map((n) => (
                  <button key={n} data-testid={`rating-${n}`} onClick={() => setRating(n)} className={`w-8 h-8 rounded-full border ${rating >= n ? "text-amber-300 border-amber-300" : "text-white/30 border-white/15"}`}>
                    ★
                  </button>
                ))}
                <div className="ml-4 flex items-center gap-2 text-xs">
                  {[
                    { id: "author_wall", label: _t("Solo autore") },
                    { id: "community", label: _t("Solo community") },
                    { id: "both", label: _t("Entrambe") },
                  ].map((d) => (
                    <button key={d.id} data-testid={`dest-${d.id}`} onClick={() => setDestination(d.id)}
                            className={`px-3 py-1 rounded-full border ${destination === d.id ? "border-[#FF5722] text-[#FF5722]" : "border-white/15 text-white/50"}`}>
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
              <textarea data-testid="review-text" value={reviewText} onChange={(e) => setReviewText(e.target.value)} rows={3} maxLength={1500}
                        placeholder={_t("Cosa ti ha lasciato questo capitolo?")}
                        className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-2 text-white text-sm" />
              <div className="flex justify-end">
                <button data-testid="review-submit" onClick={submitReview} className="btn-primary !py-2 !px-4">{_t("Pubblica recensione")}</button>
              </div>
            </section>

            <section className="pt-3 border-t border-white/5">
              <div className="text-white/70 text-sm mb-2">{_t("{0} recensioni", reviews.length)}</div>
              {reviews.map((r) => (
                <div key={r.id} className={`p-3 rounded-xl mb-2 border ${r.pinned ? "border-amber-400/40 bg-amber-500/5" : "border-white/10 bg-white/[0.02]"}`}>
                  <div className="flex justify-between">
                    <span className="text-white/80 text-sm font-semibold">
                      {r.pinned && <span className="text-amber-300 mr-1">📌</span>}
                      {r.reviewer_display_name}
                    </span>
                    <span className="text-amber-300 text-xs">{"★".repeat(r.rating)}</span>
                  </div>
                  <div className="text-white/90 text-sm mt-1 whitespace-pre-wrap">{r.text}</div>
                  {r.author_reply && (
                    <div className="mt-2 pl-3 border-l-2 border-cyan-400/30 text-xs text-cyan-200">
                      <div className="text-cyan-300 mb-0.5">{_t("Risposta dell'autore")}</div>
                      <div className="text-white/85 whitespace-pre-wrap">{r.author_reply}</div>
                    </div>
                  )}
                </div>
              ))}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

export default function RaccontiCatalog() {
  const [catalog, setCatalog] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [meAuthor, setMeAuthor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [cat, ann, me] = await Promise.all([
        api.get("/racconti/catalog"),
        api.get("/racconti/community/announcements"),
        api.get("/racconti/me/author").catch(() => ({ data: { is_author: false } })),
      ]);
      setCatalog(cat.data.catalog.filter((c) => c.state === "PUBBLICATO"));
      setAnnouncements(ann.data.announcements || []);
      setMeAuthor(me.data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  return (
    <AppLayout>
      <div className="space-y-8">
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-4xl sm:text-5xl text-white flex items-center gap-3">
              <BookOpen className="w-10 h-10 text-amber-300" />{" "}{_t("Racconti nel Tempo")}</h1>
            <p className="text-white/60 text-sm max-w-2xl mt-1">{_t("Capitoli sigillati che si aprono nelle prossime settimane. Anteprime gratuite, capitoli sbloccati con un breve spot che finanzia l'autore.")}</p>
          </div>
          <div className="flex gap-2">
            {meAuthor?.is_author ? (
              <Link to="/racconti/dashboard" data-testid="link-author-dash" className="btn-primary">{_t("Area Autore")}</Link>
            ) : (
              <Link to="/racconti/autore/registrati" data-testid="link-author-register" className="btn-primary">{_t("Diventa autore")}</Link>
            )}
          </div>
        </header>

        {announcements.length > 0 && (
          <section className="space-y-3">
            <h2 className="text-lg text-white font-display flex items-center gap-2">
              <Calendar className="w-4 h-4 text-cyan-300" />{" "}{_t("In arrivo nei prossimi 14 giorni")}</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {announcements.map((a) => (
                <div key={a.id} data-testid={`ann-${a.id}`} className="rounded-2xl border border-cyan-400/30 bg-cyan-500/5 p-4">
                  <div className="text-[10px] uppercase tracking-widest text-cyan-300">{_t("Apertura tra {0} giorni", a.days_until_open)}</div>
                  <div className="text-white text-lg font-display mt-1">{a.title}</div>
                  <div className="text-xs text-white/60">{_t("di {0}", a.author_pen_name)}</div>
                  <div className="text-sm text-white/70 mt-2 line-clamp-2">{a.synopsis}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="space-y-3">
          <h2 className="text-lg text-white font-display">{_t("Catalogo")}</h2>
          {loading ? (
            <div className="text-white/50 text-sm">{_t("Carico il catalogo…")}</div>
          ) : catalog.length === 0 ? (
            <div className="text-white/50 text-sm" data-testid="empty-catalog">{_t("Nessun capitolo pubblicato al momento.")}</div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {catalog.map((c) => <ChapterCard key={c.id} c={c} onOpen={setDetail} />)}
            </div>
          )}
        </section>

        {detail && <ChapterModal chapter={detail} onClose={() => setDetail(null)} onDownloaded={load} />}
      </div>
    </AppLayout>
  );
}
